/**
 * F11 Task 19 — Admin B2B Accounts list page.
 *
 * Route: /b2b-accounts
 * Permission: b2b_accounts:read
 *
 * Browse all business accounts on BraveMove. Search by legal name, display name,
 * or owner email. Status filter chips (Active / Suspended / Disabled / All).
 * Cursor pagination, 25 rows per page. Row click → /b2b-accounts/:id.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  type BadgeTone,
  Banner,
  Chip,
  EmptyState,
  SearchInput,
  Skeleton,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type { AdminPageMeta, B2bAccountStatus } from '@bravemove/types';
import {
  adminB2bAccountApi,
  type B2bAccountListItem,
  type B2bAccountListOpts,
} from '../../api/b2b-account.js';
import { translateApiError } from '../../auth/errors.js';
import { PageHeader } from '../../components/PageHeader';
import { AdminPagination } from '../../components/AdminPagination';

const PAGE_LIMIT = 25;
type StatusFilter = B2bAccountStatus | 'all';
const DEFAULT_STATUS: StatusFilter = 'active';
const SEARCH_DEBOUNCE_MS = 300;

const FILTER_CHIPS: ReadonlyArray<{ value: StatusFilter; labelKey: string }> = [
  { value: 'active', labelKey: 'admin:claim.b2b_accounts.filter_status_active' },
  { value: 'suspended', labelKey: 'admin:claim.b2b_accounts.filter_status_suspended' },
  { value: 'disabled', labelKey: 'admin:claim.b2b_accounts.filter_status_disabled' },
  { value: 'all', labelKey: 'admin:claim.b2b_accounts.filter_status_all' },
];

const STATUS_TONE: Record<B2bAccountStatus, BadgeTone> = {
  active: 'success',
  suspended: 'warning',
  disabled: 'danger',
};

function formatRelative(iso: string | null, locale: string): string {
  if (!iso) return '—';
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return '—';
  const diffMs = target - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const absMin = Math.abs(diffMin);
    if (absMin < 60) return rtf.format(diffMin, 'minute');
    const diffHour = Math.round(diffMin / 60);
    if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
    const diffDay = Math.round(diffHour / 24);
    return rtf.format(diffDay, 'day');
  } catch {
    return iso;
  }
}

function buildListOpts(raw: {
  status: StatusFilter;
  q?: string | null;
  page: number;
}): B2bAccountListOpts {
  const opts: Record<string, string | number> = { perPage: PAGE_LIMIT, page: raw.page };
  if (raw.status) opts.status = raw.status;
  if (raw.q && raw.q.trim()) opts.q = raw.q.trim();
  return opts as unknown as B2bAccountListOpts;
}

export function B2bAccountsListPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = (searchParams.get('status') as StatusFilter | null) ?? DEFAULT_STATUS;
  const qParam = searchParams.get('q') ?? '';
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  // Local search input value (debounced into the URL query param).
  const [searchValue, setSearchValue] = useState<string>(qParam);

  const [rows, setRows] = useState<readonly B2bAccountListItem[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debounce search input → URL search-param.
  useEffect(() => {
    if (searchValue === qParam) return undefined;
    const handle = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchValue.trim()) next.set('q', searchValue.trim());
        else next.delete('q');
        next.delete('page');
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchValue, qParam, setSearchParams]);

  const fetchList = useCallback(
    async (opts: B2bAccountListOpts) => {
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const data = await adminB2bAccountApi.listAccounts(opts);
        setRows(data.items);
        setPagination(data.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList(
      buildListOpts({
        status: statusParam,
        q: qParam,
        page: pageParam,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusParam, qParam, pageParam]);

  const onStatusChange = (value: StatusFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== DEFAULT_STATUS) next.set('status', value);
      else next.delete('status');
      next.delete('page');
      return next;
    });
  };

  const goToPage = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  };

  const columns = useMemo<readonly TableColumn<B2bAccountListItem>[]>(
    () => [
      {
        key: 'displayName',
        header: t('admin:claim.b2b_accounts.column_display_name'),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-[13px] text-ink">{row.displayName}</span>
            <span className="text-[11.5px] text-ink-3">{row.legalBusinessName}</span>
          </div>
        ),
      },
      {
        key: 'owner',
        header: t('admin:claim.b2b_accounts.column_owner'),
        cell: (row) => (
          <span className="font-mono text-[11.5px] text-ink-2">
            {row.primaryOwnerUserId.slice(0, 8)}…
          </span>
        ),
      },
      {
        key: 'status',
        header: t('admin:claim.b2b_accounts.column_status'),
        cell: (row) => (
          <Badge tone={STATUS_TONE[row.status]} size="sm">
            {t(`admin:claim.b2b_accounts.filter_status_${row.status}`)}
          </Badge>
        ),
      },
      {
        key: 'venueCount',
        header: t('admin:claim.b2b_accounts.column_venues'),
        align: 'end',
        cell: (row) => <span className="font-mono text-[12.5px] text-ink-2">{row.venueCount}</span>,
      },
      {
        key: 'activeClaimCount',
        header: t('admin:claim.b2b_accounts.column_active_claims'),
        align: 'end',
        cell: (row) => (
          <span className="font-mono text-[12.5px] text-ink-2">{row.activeClaimCount}</span>
        ),
      },
      {
        key: 'createdAt',
        header: t('admin:claim.b2b_accounts.column_created'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-3">{formatRelative(row.createdAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin:claim.b2b_accounts.title')}
        subtitle={t('admin:claim.b2b_accounts.subtitle')}
      />

      {/* Filter row: status chips + search input */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          role="tablist"
          aria-label={t('admin:claim.b2b_accounts.filter_status_label')}
          className="flex flex-wrap items-center gap-2"
        >
          {FILTER_CHIPS.map((chip) => (
            <Chip
              key={chip.value}
              selected={statusParam === chip.value}
              onClick={() => onStatusChange(chip.value)}
            >
              {t(chip.labelKey)}
            </Chip>
          ))}
        </div>
        <div className="w-full sm:w-80">
          <SearchInput
            placeholder={t('admin:claim.b2b_accounts.search_placeholder')}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            aria-label={t('admin:claim.b2b_accounts.search_placeholder')}
          />
        </div>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="b2b-accounts-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="team" size={36} color="currentColor" />}
          title={t('admin:claim.b2b_accounts.title')}
          description={t('admin:claim.b2b_accounts.subtitle')}
        />
      ) : (
        <Table
          data={rows as B2bAccountListItem[]}
          columns={columns as TableColumn<B2bAccountListItem>[]}
          rowKey={(row) => row.id}
          onRowPress={(row) => navigate(`/b2b-accounts/${row.id}`)}
          empty={t('admin:claim.b2b_accounts.title')}
        />
      )}

      {pagination && !loading && rows.length > 0 ? (
        <AdminPagination
          pagination={pagination}
          shownCount={rows.length}
          onChange={goToPage}
          testId="b2b-accounts-pagination"
        />
      ) : null}
    </div>
  );
}
